import { supabase } from '../supabaseClient';
import { readFunctionError } from '../utils/functionError';

/**
 * The person's side of an email reset: the page an emailed link opens.
 * Somebody managing staff asked for the change (staffService.requestEmailReset);
 * here its owner agrees to it, and until they do nothing has moved.
 */

/** Why a link cannot be used at all. */
export type EmailResetFailure =
  | 'invalid'
  | 'used'
  | 'cancelled'
  | 'expired'
  /** Too many wrong passwords. */
  | 'locked'
  /** The account is not active. */
  | 'revoked'
  /** Another account took the address after the link was sent. */
  | 'taken';

/** A try that did not confirm, on a link that is still good. */
export type EmailResetRetry = 'wrong_password' | 'password_required';

export interface EmailResetDetails {
  newEmail: string;
  companyName: string | null;
  /** False for somebody invited who has never signed in: they have no password yet. */
  requiresPassword: boolean;
  expiresAt: string | null;
}

export interface ConfirmedEmailReset {
  email: string;
  /** Set when they have not finished setting up: the invite to carry on with. */
  inviteToken: string | null;
}

export class EmailResetError extends Error {
  reason: EmailResetFailure | EmailResetRetry;

  constructor(reason: EmailResetFailure | EmailResetRetry, message?: string) {
    super(message ?? reason);
    this.name = 'EmailResetError';
    this.reason = reason;
  }
}

const FAILURES: EmailResetFailure[] = ['invalid', 'used', 'cancelled', 'expired', 'locked', 'revoked', 'taken'];

const toReason = (value: unknown): EmailResetFailure | EmailResetRetry => {
  if (value === 'wrong_password' || value === 'password_required') return value;
  return FAILURES.includes(value as EmailResetFailure) ? value as EmailResetFailure : 'invalid';
};

export const emailResetService = {
  async fetchReset(token: string): Promise<EmailResetDetails> {
    const { data, error } = await supabase.rpc('get_staff_email_reset_by_token', { p_token: token });
    if (error) throw error;

    const payload = data as {
      success?: boolean;
      reason?: string;
      reset?: Record<string, unknown>;
    } | null;

    if (!payload?.success || !payload.reset) {
      throw new EmailResetError(toReason(payload?.reason));
    }

    return {
      newEmail: (payload.reset.new_email as string | null) ?? '',
      companyName: (payload.reset.company_name as string | null) ?? null,
      requiresPassword: payload.reset.requires_password !== false,
      expiresAt: (payload.reset.expires_at as string | null) ?? null,
    };
  },

  /**
   * Agrees to the change. The password is the one they sign in with today, and
   * is what makes this theirs to agree to; it is not changed.
   */
  async confirm(token: string, password: string | null): Promise<ConfirmedEmailReset> {
    const { data, error } = await supabase.functions.invoke('confirm-email-reset', {
      body: password ? { token, password } : { token },
    });

    if (error) {
      const { message, reason } = await readFunctionError(error);
      if (reason) throw new EmailResetError(toReason(reason), message ?? undefined);
      throw new Error(message ?? 'Failed to change email');
    }

    const result = data as { email?: string; inviteToken?: string | null } | null;
    return {
      email: result?.email ?? '',
      inviteToken: result?.inviteToken || null,
    };
  },
};
